import { createFileRoute, useNavigate, Link } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { authApi, ApiError } from '@wordpuppi/shared';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ThemeToggle } from '@/components/ThemeToggle';
import { SocialLoginButtons } from '@/components/SocialLoginButtons';
import logoBlack from '@/assets/logo-black-300w.webp';
import logoWhite from '@/assets/logo-white-300w.webp';

export const Route = createFileRoute('/register')({
  // AB#537: a super-admin signup invite lands here as ?invite=<token>&email=<addr>;
  // the token lets that email register even while registration is closed.
  validateSearch: (search: Record<string, unknown>): { invite?: string; email?: string } => ({
    invite: typeof search.invite === 'string' ? search.invite : undefined,
    email: typeof search.email === 'string' ? search.email : undefined,
  }),
  component: RegisterPage,
});

// AB#196: Cloudflare Turnstile is render-gated on a build-time site key. Unset
// (as on prod today) → no widget, no token, register works exactly as before.
// Set it (VITE_TURNSTILE_SITE_KEY) only alongside the server-side secret.
const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined;

function RegisterPage() {
  const navigate = useNavigate();
  const { invite, email: invitedEmail } = Route.useSearch();
  const [name, setName] = useState('');
  const [email, setEmail] = useState(invitedEmail ?? '');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [closed, setClosed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Load Cloudflare's Turnstile script once, only when a site key is configured.
  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) return;
    if (document.querySelector('script[data-turnstile]')) return;
    const s = document.createElement('script');
    s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
    s.async = true;
    s.defer = true;
    s.setAttribute('data-turnstile', '');
    document.head.appendChild(s);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const form = e.currentTarget as HTMLFormElement;
    setError('');

    if (name.trim().length === 0) {
      setError('Please enter your name.');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }

    // Turnstile auto-injects a hidden <input name="cf-turnstile-response"> into
    // the form; read it before the await (currentTarget nulls out afterward).
    // Absent when no widget rendered → undefined → register omits the field.
    const turnstileToken = TURNSTILE_SITE_KEY
      ? form.querySelector<HTMLInputElement>('[name="cf-turnstile-response"]')?.value || undefined
      : undefined;

    setIsSubmitting(true);
    try {
      await authApi.register({ name: name.trim(), email, password, turnstileToken, inviteToken: invite });
      // Anti-enumeration: the backend returns 200 even for duplicate emails, so
      // we always route to the "check your email" interstitial (never "account created").
      navigate({ to: '/verify-email', search: { email } });
    } catch (err) {
      // Turnstile tokens are single-use — reset the widget so a retry gets a
      // fresh one instead of resubmitting a spent (rejected) token.
      const w = window as { turnstile?: { reset: () => void } };
      w.turnstile?.reset();
      // Registration is env-gated in production: 403 => registration_closed.
      if (err instanceof ApiError && err.status === 403) {
        setClosed(true);
      } else {
        setError(err instanceof Error ? err.message : 'Registration failed');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center p-4">
      <div className="absolute top-4 right-4">
        <ThemeToggle />
      </div>
      <Card className="w-full max-w-[400px]">
        <CardHeader>
          <div className="flex justify-center">
            <img
              src={logoBlack}
              alt="WordPuppi"
              className="dark:hidden"
              style={{ height: 'calc(var(--spacing) * 14)' }}
            />
            <img
              src={logoWhite}
              alt="WordPuppi"
              className="hidden dark:block"
              style={{ height: 'calc(var(--spacing) * 14)' }}
            />
          </div>
          <CardTitle className="text-center text-2xl">Create your account</CardTitle>
        </CardHeader>
        <CardContent>
          {closed ? (
            <div className="space-y-4 text-center">
              <div className="p-3 bg-muted rounded-lg text-sm text-muted-foreground">
                Registration is currently closed. Please check back soon or contact
                support if you were expecting an invitation.
              </div>
              <Button asChild variant="outline" className="w-full">
                <Link to="/login">Back to sign in</Link>
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="p-3 bg-destructive/10 text-destructive rounded-lg text-sm">
                  {error}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  autoComplete="name"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={8}
                  autoComplete="new-password"
                />
                <p className="text-xs text-muted-foreground">At least 8 characters.</p>
              </div>

              {TURNSTILE_SITE_KEY && (
                <div className="cf-turnstile" data-sitekey={TURNSTILE_SITE_KEY} />
              )}

              <Button type="submit" disabled={isSubmitting} className="w-full">
                {isSubmitting ? 'Creating account...' : 'Create account'}
              </Button>

              <SocialLoginButtons />

              <p className="text-center text-sm text-muted-foreground">
                Already have an account?{' '}
                <Link to="/login" className="text-primary underline-offset-4 hover:underline">
                  Sign in
                </Link>
              </p>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
